import { useState, useRef, useEffect, useMemo, useCallback, PropsWithChildren } from 'react';
import CardContent, { CardContentProps } from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import {
  AutocompleteInput,
  DateFieldProps,
  FormDataConsumer,
  Labeled,
  Link,
  Loading,
  required,
  SelectInput,
  SelectInputProps,
  TextField,
  TextInput,
  useGetList,
  useGetManyReference,
  useInput,
} from 'react-admin';
import { useNativeLineItem } from './hooks/useNativeLineItem';
import { useQuery } from '@apollo/client';
import {
  TargetListsForDropdownDocument,
  TargetListsOrderBy,
  TargetListFilter,
} from 'src/generated/client';
import { ESTDateField } from '../custom-ra-fields/est-date-field';
import { DateTimePicker } from '@mui/x-date-pickers';
import { zonedTimeToUtc } from 'date-fns-tz';
import { NEW_YORK_TIMEZONE, preventEnterSubmit, toNewYorkTime } from './form-utils';

export const FormHeader = ({ children }: PropsWithChildren) => (
  <Typography variant="h5" gutterBottom>
    {children}
  </Typography>
);

export const FormSectionHeader = ({ children }: PropsWithChildren) => (
  <Typography variant="h6" gutterBottom sx={{ marginTop: '1.5rem' }}>
    {children}
  </Typography>
);

export const FormDivider = () => (
  <Box marginBottom={3}>
    <Divider />
  </Box>
);

const EnterSubmitBlockingRoot = styled(CardContent, {
  name: 'EnterSubmitBlockingSimpleFormContent',
  overridesResolver: (props, styles) => styles.root,
})(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    paddingBottom: '5em',
  },
}));

export const EnterSubmitBlockingSimpleFormContent = ({ onKeyDown, ...props }: CardContentProps) => (
  <EnterSubmitBlockingRoot
    {...props}
    onKeyDown={event => {
      onKeyDown?.(event);
      if (!event.defaultPrevented) preventEnterSubmit(event);
    }}
  />
);

type DateInputWrapperProps = {
  source: string;
  label: string;
  defaultValue?: Date;
  minDateTime?: Date;
  maxDateTime?: Date;
};

const DateInputWrapper = ({ source, label, defaultValue, minDateTime, maxDateTime }: DateInputWrapperProps) => {
  const {
    field: { value, onChange },
  } = useInput({ source });

  const dateTimeValue = value ?? defaultValue;

  const handleChange = (newValue: Date | null) => {
    if (newValue) {
      // Convert the selected datetime to UTC before storing it in the form
      const utcDate = zonedTimeToUtc(newValue, NEW_YORK_TIMEZONE);
      onChange(utcDate.toISOString());
    } else {
      onChange(null);
    }
  };

  return (
    <DateTimePicker
      label={label}
      value={toNewYorkTime(dateTimeValue)}
      onChange={handleChange}
      minDateTime={minDateTime}
      maxDateTime={maxDateTime}
    />
  );
};

export type DateInputGroupProps = {
  minStartsAt: Date;
  maxEndsAt: Date;
};

export const DateInputGroup = ({ minStartsAt, maxEndsAt }: DateInputGroupProps) => (
  <Box display="flex">
    <Box marginRight="1rem">
      <DateInputWrapper source="startsAt" label="Starts At" defaultValue={minStartsAt} maxDateTime={maxEndsAt} />
    </Box>
    <DateInputWrapper source="endsAt" label="Ends At" defaultValue={maxEndsAt} minDateTime={minStartsAt} />
  </Box>
);

export const TitleInput = ({ label }: { label?: string }) => (
  <TextInput label={label} source="title" fullWidth resettable multiline validate={required()} />
);

interface SelectInputChoice {
  id: string;
  name: string;
}

const byNameAsc = (a: SelectInputChoice, b: SelectInputChoice) => {
  if (a.name < b.name) return -1;
  if (a.name > b.name) return 1;
  return 0;
};

export const LineItemTargetListInput = ({ lineItemId }: { lineItemId: number }) => {
  const [filterValue, setFilterValue] = useState('');
  // Cache selected item so it persists in choices when query results change
  const selectedItemRef = useRef<SelectInputChoice | null>(null);

  // Get the current form value for targetListId (populated on edit)
  const { field } = useInput({ source: 'targetListId' });
  const existingTargetListId = field.value;

  // Fetch the existing target list by ID so it appears in choices on edit
  const { data: existingTargetListData } = useQuery(TargetListsForDropdownDocument, {
    variables: {
      first: 1,
      offset: 0,
      orderBy: [TargetListsOrderBy.NameAsc],
      filter: { id: { equalTo: existingTargetListId } },
    },
    skip: !existingTargetListId,
  });

  // Cache the existing target list when it loads
  useEffect(() => {
    const node = existingTargetListData?.targetLists?.nodes?.[0];
    if (node && !selectedItemRef.current) {
      selectedItemRef.current = { id: node.id, name: node.name };
    }
  }, [existingTargetListData]);

  // Fetch existing banner targetings for this line item to exclude their target lists
  const bannerTargetingResult = useGetList('bannerTargeting', {
    pagination: { page: 1, perPage: 5000 },
    sort: { field: 'startsAt', order: 'DESC' },
    filter: { placementsLineItemId: lineItemId.toString() },
  });

  // Build the filter for GraphQL query
  // Always exclude archived target lists; when searching, filter by name or ID
  const filter = useMemo((): TargetListFilter => {
    const baseFilter: TargetListFilter = { isArchived: { equalTo: false } };

    if (filterValue.length >= 2) {
      const parsedId = parseInt(filterValue, 10);
      const isValidId = !isNaN(parsedId) && parsedId.toString() === filterValue.trim();

      if (isValidId) {
        return {
          ...baseFilter,
          or: [
            { id: { equalTo: parsedId } },
            { name: { includesInsensitive: filterValue } }
          ]
        };
      }

      return { ...baseFilter, name: { includesInsensitive: filterValue } };
    }
    return baseFilter;
  }, [filterValue]);

  const orderBy = useMemo((): TargetListsOrderBy[] => {
    if (filterValue.length < 2) {
      return [TargetListsOrderBy.UploadedAtDesc];
    }
    return [TargetListsOrderBy.NameAsc];
  }, [filterValue]);

  // Use lightweight Apollo query with server-side filtering
  const { data: targetListsData, loading: targetListsLoading, previousData } = useQuery(TargetListsForDropdownDocument, {
    variables: {
      first: 100,
      offset: 0,
      orderBy,
      filter,
    },
  });

  const currentData = targetListsLoading ? previousData : targetListsData;

  // Track selected item in cache whenever query results change
  const handleChange = useCallback((value: number | string) => {
    const allNodes = currentData?.targetLists?.nodes ?? [];
    const match = allNodes.find((tl: SelectInputChoice) => tl.id === value);
    selectedItemRef.current = match ?? null;
  }, [currentData?.targetLists?.nodes]);

  // Filter out target lists already associated with this line item,
  // and ensure the currently selected item is always in choices
  const choices = useMemo(() => {
    const allTargetLists = currentData?.targetLists?.nodes ?? [];
    const existingTargetListIds = new Set(
      bannerTargetingResult?.data?.map(each => each.targetListId) || []
    );
    const filtered = allTargetLists
      .filter((tl: SelectInputChoice) => !existingTargetListIds.has(tl.id));

    // Ensure selected item stays in choices even if not in current query results
    if (selectedItemRef.current && !filtered.some((tl: SelectInputChoice) => tl.id === selectedItemRef.current!.id)) {
      filtered.unshift(selectedItemRef.current);
    }

    return filtered;
  }, [currentData?.targetLists?.nodes, bannerTargetingResult?.data]);

  return (
    <Box display="flex">
      <Box>
        <AutocompleteInput
          label="Target List"
          source="targetListId"
          choices={choices as SelectInputChoice[]}
          sx={{ width: 400, '& + .MuiAutocomplete-popper': { minWidth: 400 }, '& + .MuiAutocomplete-popper .MuiAutocomplete-option': { wordBreak: 'break-all' } }}
          validate={validateTargetList}
          isLoading={targetListsLoading || bannerTargetingResult.isLoading}
          onChange={handleChange}
          debounce={300}
          filterToQuery={searchText => searchText}
          setFilter={setFilterValue}
          shouldRenderSuggestions={() => true}
          noOptionsText="No target lists found"
          helperText={filterValue.length === 0
            ? "Showing 100 most recently created. Type to search by name or ID."
            : filterValue.length === 1
            ? "Type at least 2 characters to search"
            : undefined
          }
        />
      </Box>
    </Box>
  );
};

const validateTargetList = (value: [] | undefined) => {
  return !value ? 'Target List is required' : undefined;
};

interface CampaignLineItemsSelectInputProps extends SelectInputProps {
  campaignId: string;
}

export const CampaignLineItemsSelectInput = ({ campaignId, ...rest }: CampaignLineItemsSelectInputProps) => {
  // The Placements.IO API shut off at contract end, so the 'placementCampaigns'
  // proxy read is gone. Every campaign — imported or native — has a native row,
  // and the creative forms resolve the parent line item natively first, so
  // campaignId is always the native campaign UUID; source sibling line items
  // from the native 'lineItems' resource.
  //
  // Choice ids preserve the stored placementsLineItemId semantics: an imported
  // line item keeps its legacy numeric id (externalPioId) so existing creative
  // rows still match, while a natively-built line item uses its UUID (which the
  // write path resolves to the lineItemId FK).
  const {
    data: nativeLineItems,
    isLoading,
    error,
  } = useGetManyReference(
    'lineItems',
    {
      target: 'campaignId',
      id: campaignId,
      pagination: { page: 1, perPage: 1000 },
      sort: { field: 'name', order: 'ASC' },
      filter: { includeArchived: true },
    },
    { enabled: Boolean(campaignId) },
  );

  if (isLoading) {
    return <Loading />;
  }
  if (error) {
    return <p>ERROR</p>;
  }

  // Bail only when the line-items reference never resolved. A campaign that
  // loaded but has zero line items still renders an empty SelectInput — do NOT
  // gate on empty choices. (MD-5145 / FIX D)
  if (!nativeLineItems) {
    return null;
  }

  const choices = nativeLineItems.map(lineItem => ({
    ...lineItem,
    id: lineItem.externalPioId ?? lineItem.id,
  })) as SelectInputChoice[];
  choices.sort(byNameAsc);

  return (
    <Box display="flex" flexWrap="wrap" rowGap="0.5rem" columnGap="1rem">
      <Box>
        <SelectInput
          label="Line Item Name"
          source="placementsLineItemId"
          choices={choices}
          {...rest}
          validate={required()}
        />
      </Box>
      <FormDataConsumer>
        {({ formData }) => (
          <>
            <Box display="flex" gap="1rem">
              <CurrentLineItemDateField
                lineItemId={formData?.placementsLineItemId}
                label="Line Item Start Date"
                source="startDate"
              />
              <CurrentLineItemDateField
                lineItemId={formData?.placementsLineItemId}
                label="Line Item End Date"
                source="endDate"
              />
            </Box>
            <CurrentLineItemLinkedNameField
              lineItemId={formData?.placementsLineItemId}
              label="Line Item Name"
              source="name"
            />
          </>
        )}
      </FormDataConsumer>
    </Box>
  );
};

interface CurrentLineItemFieldProps extends DateFieldProps {
  lineItemId: string;
  source: string;
  label: string;
}

const CurrentLineItemDateField = ({ lineItemId, source, label }: CurrentLineItemFieldProps) => {
  const { lineItem, isLoading, error } = useNativeLineItem(lineItemId);

  if (isLoading) {
    return <Loading />;
  }
  if (error) {
    return <p>ERROR</p>;
  }
  if (!lineItem) return null;

  return (
    <Labeled label={label}>
      <ESTDateField record={lineItem} source={source} variant="body1" />
    </Labeled>
  );
};

const CurrentLineItemLinkedNameField = ({ lineItemId, source, label }: CurrentLineItemFieldProps) => {
  // Link to the native show route regardless of which id form came in — the
  // legacy '/line_items/:id/show' page can no longer load (PIO API shut off),
  // and the resolved native record always carries the native UUID.
  const { lineItem, isLoading, error } = useNativeLineItem(lineItemId);

  if (isLoading) {
    return <Loading />;
  }
  if (error) {
    return <p>ERROR</p>;
  }
  if (!lineItem) return null;

  return (
    <Labeled label={label}>
      <Link to={`/lineItems/${lineItem.id}/show`}>
        <TextField record={lineItem} source={source} variant="body1" />
      </Link>
    </Labeled>
  );
};

export const CurrentLineItemDateInputGroup = () => (
  <FormDataConsumer>
    {({ formData }) => {
      return <DateInputGroupForLineItem lineItemId={formData.placementsLineItemId} />;
    }}
  </FormDataConsumer>
);

type DateInputGroupForLineItemProps = {
  lineItemId: string;
};

const DateInputGroupForLineItem = ({ lineItemId }: DateInputGroupForLineItemProps) => {
  const { lineItem, isLoading, error } = useNativeLineItem(lineItemId);
  if (isLoading) {
    return <Loading />;
  }
  if (error) {
    return <p>ERROR</p>;
  }
  if (!lineItem) return null;

  const lineItemStartDate = new Date(lineItem.startDate);
  const lineItemEndDate = new Date(lineItem.endDate);

  return <DateInputGroup minStartsAt={lineItemStartDate} maxEndsAt={lineItemEndDate} />;
};
